import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import * as authService from "api/authService";
import { AuthState, LoginRequest } from "types/auth";
import { User, UserPoints } from "common/models/types";
import { log, LogLevel } from "utils/LogUtil";
import { setUserProperty, eventLog } from "utils/AmplitudeUtil";
import { fetchUserPoints } from "api/pointsService";
import { HybridStreamManager } from "services/HybridStreamManager";
import { unregisterFcmToken } from "services/fcmService";
import { hasAnyPremiumRole } from "common/constants/roles";

// 광의의 Premium 사용자 판별 — 유료 프리미엄 OR 면허번호/약사회 권한
const checkIsPremiumUser = (user: User | null): boolean => {
  if (!user) return false;
  return Boolean(user.isPremium) || hasAnyPremiumRole(user.roles);
};

// 유료 프리미엄 전용 판별
const checkIsPaidPremiumUser = (user: User | null): boolean => {
  if (!user) return false;
  return Boolean(user.isPremium);
};

// 초기 상태
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isPremiumUser: false,
  isPaidPremiumUser: false,
  isLoading: false,
  error: null,
};

// 사용자 정보 가져오기 비동기
export const fetchUserInfo = createAsyncThunk(
  "user/fetchUserInfo",
  async (_, { rejectWithValue }) => {
    try {
      const userData = await authService.getCurrentUser();
      log(LogLevel.VERBOSE, "fetchUserInfo 성공 - userData:", userData);

      // roles 확인 및 기본값 설정
      if (!userData.roles || !Array.isArray(userData.roles)) {
        console.warn("roles 필드가 없거나 배열이 아님, 기본값 설정:", userData);
        userData.roles = ["user"];
      }

      return userData;
    } catch (error) {
      const err = error as AxiosError;
      log(LogLevel.ERROR, "fetchUserInfo 에러:", err);
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue("사용자 정보를 가져오는데 실패했습니다.");
      }
    }
  }
);

// 로그인 비동기 액션
export const login = createAsyncThunk(
  "user/login",
  async (credentials: LoginRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await authService.login(credentials);
      log(LogLevel.VERBOSE, "login 응답:", response);

      // 응답에 error 필드가 있는지 확인
      if (response && typeof response === "object" && "error" in response && response.error) {
        log(LogLevel.ERROR, "API 로그인 응답에 error 포함:", response.error);
        return rejectWithValue(
          typeof response.error === "string" ? response.error : "로그인 중 오류가 발생했습니다."
        );
      }

      // user 객체 유효성 검사
      if (!response.user || typeof response.user !== "object") {
        log(LogLevel.ERROR, "로그인 응답에 유효한 user 객체 없음:", response);
        return rejectWithValue("로그인 응답에 유효한 사용자 정보가 없습니다.");
      }

      // roles 확인 및 기본값 설정
      if (!response.user.roles || !Array.isArray(response.user.roles)) {
        console.warn("login - roles 필드가 없거나 배열이 아님, 기본값 설정:", response.user);
        response.user.roles = ["user"];
      }

      // 로그인 후 사용자 정보 저장
      return response;
    } catch (error) {
      const err = error as AxiosError;
      log(LogLevel.ERROR, "login 에러:", err);
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue("로그인에 실패했습니다.");
      }
    }
  }
);

// 로그아웃 비동기 액션
export const logout = createAsyncThunk("user/logout", async (_, { rejectWithValue }) => {
  try {
    // 1. FCM 토큰 삭제 (API 필요, 세션 유효)
    await unregisterFcmToken();
    // 2. Stream 정리 (로컬)
    HybridStreamManager.getInstance().cleanup();
    // 3. 세션 무효화
    await authService.logout();
    return null;
  } catch (error) {
    const err = error as AxiosError;
    if (err.response && err.response.data) {
      return rejectWithValue(err.response.data);
    } else {
      return rejectWithValue("로그아웃에 실패했습니다.");
    }
  }
});

// 사용자 포인트 업데이트 비동기 액션
export const updateUserPoints = createAsyncThunk(
  "user/updateUserPoints",
  async (_, { rejectWithValue }) => {
    try {
      const points = await fetchUserPoints();
      log(LogLevel.VERBOSE, "updateUserPoints 성공 - points:", points);
      return points;
    } catch (error) {
      const err = error as AxiosError;
      log(LogLevel.ERROR, "updateUserPoints 에러:", err);
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      } else {
        return rejectWithValue("포인트 정보를 가져오는데 실패했습니다.");
      }
    }
  }
);

// 사용자 슬라이스
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // 사용자 정보 수동 설정
    setUser(state, action: PayloadAction<User>) {
      log(LogLevel.VERBOSE, "[userSlice] setUser 액션 호출:", {
        previousState: {
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user,
        },
        newUser: action.payload,
        timestamp: new Date().toISOString(),
      });

      state.user = action.payload;
      state.isAuthenticated = true;
      state.isPremiumUser = checkIsPremiumUser(action.payload);
      state.isPaidPremiumUser = checkIsPaidPremiumUser(action.payload);
      state.error = null;

      log(LogLevel.VERBOSE, "[userSlice] setUser 액션 완료:", {
        newState: {
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user,
          userRoles: state.user?.roles,
          isPremiumUser: state.isPremiumUser,
        },
        timestamp: new Date().toISOString(),
      });
    },
    // 사용자 정보 초기화
    resetUser(state) {
      state.user = null;
      state.isAuthenticated = false;
      state.isPremiumUser = false;
      state.isPaidPremiumUser = false;
      state.error = null;
    },
    // 에러 초기화
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // 사용자 정보 가져오기
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        log(LogLevel.VERBOSE, "[userSlice] fetchUserInfo.pending");
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        log(LogLevel.VERBOSE, "[userSlice] fetchUserInfo.fulfilled - payload:", {
          payload: action.payload,
          timestamp: new Date().toISOString(),
        });
        state.isLoading = false;

        // user 객체와 roles 배열이 있는지 확인
        if (action.payload && typeof action.payload === "object") {
          // roles가 없거나 배열이 아닌 경우 기본값 설정
          const userData = {
            ...action.payload,
            roles: Array.isArray(action.payload.roles) ? action.payload.roles : ["user"],
          };

          log(LogLevel.VERBOSE, "[userSlice] fetchUserInfo.fulfilled - setUser 호출 전:", {
            userData,
            isPremium: checkIsPremiumUser(userData),
            timestamp: new Date().toISOString(),
          });

          // setUser 액션을 통해 상태 업데이트
          userSlice.caseReducers.setUser(state, {
            type: "user/setUser",
            payload: userData,
          });

          eventLog("로그인 성공", {});
        } else {
          log(LogLevel.ERROR, "[userSlice] fetchUserInfo.fulfilled - 유효하지 않은 user 데이터:", {
            payload: action.payload,
            timestamp: new Date().toISOString(),
          });
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(fetchUserInfo.rejected, (state, action) => {
        state.isLoading = false;

        // 에러 처리 개선 - 객체인 경우 message 속성을 추출하거나 문자열로 변환
        if (action.payload) {
          if (typeof action.payload === "string") {
            // state.error = action.payload;
          } else if (typeof action.payload === "object") {
            // any로 캐스팅하여 message 속성 접근
            const errorObj = action.payload as any;
            // state.error = errorObj.message || "사용자 정보를 가져오는데 실패했습니다.";
          } else {
            // state.error = "알 수 없는 오류가 발생했습니다.";
          }
        } else {
          // state.error = "사용자 정보를 가져오는데 실패했습니다.";
        }
      });

    // 로그인
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        log(LogLevel.VERBOSE, "login.fulfilled - payload:", action.payload);
        state.isLoading = false;

        // 응답에서 user 객체 확인
        if (action.payload && action.payload.user && typeof action.payload.user === "object") {
          // roles가 없거나 배열이 아닌 경우 기본값 설정
          const userData = {
            ...action.payload.user,
            roles: Array.isArray(action.payload.user.roles) ? action.payload.user.roles : ["user"],
          };

          // setUser 액션을 통해 상태 업데이트
          userSlice.caseReducers.setUser(state, {
            type: "user/setUser",
            payload: userData,
          });
          
          log(LogLevel.VERBOSE, "login.fulfilled - Premium 상태:", {
            isPremium: state.isPremiumUser,
            roles: userData.roles
          });
        } else {
          log(LogLevel.ERROR, "login.fulfilled - 유효하지 않은 user 데이터:", action.payload);
          state.user = null;
          state.isAuthenticated = false;
          state.error = "응답에 유효한 사용자 정보가 없습니다.";
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;

        // 에러 처리 개선 - 객체인 경우 message 속성을 추출하거나 문자열로 변환
        if (action.payload) {
          if (typeof action.payload === "string") {
            state.error = action.payload;
          } else if (typeof action.payload === "object") {
            // any로 캐스팅하여 message 속성 접근
            const errorObj = action.payload as any;
            state.error = errorObj.message || "로그인에 실패했습니다.";
          } else {
            state.error = "알 수 없는 오류가 발생했습니다.";
          }
        } else {
          state.error = "로그인에 실패했습니다.";
        }
      });

    // 로그아웃
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.isPremiumUser = false;
        state.isPaidPremiumUser = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;

        // 에러 처리 개선 - 객체인 경우 message 속성을 추출하거나 문자열로 변환
        if (action.payload) {
          if (typeof action.payload === "string") {
            state.error = action.payload;
          } else if (typeof action.payload === "object") {
            // any로 캐스팅하여 message 속성 접근
            const errorObj = action.payload as any;
            state.error = errorObj.message || "로그아웃에 실패했습니다.";
          } else {
            state.error = "알 수 없는 오류가 발생했습니다.";
          }
        } else {
          state.error = "로그아웃에 실패했습니다.";
        }
      });

    // 사용자 포인트 업데이트
    builder
      .addCase(updateUserPoints.pending, (state) => {
        log(LogLevel.VERBOSE, "[userSlice] updateUserPoints.pending");
        // 포인트 업데이트는 백그라운드에서 실행되므로 전역 로딩 상태는 변경하지 않음
      })
      .addCase(updateUserPoints.fulfilled, (state, action) => {
        log(LogLevel.VERBOSE, "[userSlice] updateUserPoints.fulfilled - payload:", {
          payload: action.payload,
          timestamp: new Date().toISOString(),
        });
        
        // 사용자가 로그인된 상태에서만 포인트 정보 업데이트
        if (state.user && action.payload) {
          state.user.userPoints = action.payload;
          log(LogLevel.VERBOSE, "[userSlice] 사용자 포인트 업데이트 완료:", {
            currentPoints: action.payload.currentPoints,
            totalEarnedPoints: action.payload.totalEarnedPoints,
            totalUsedPoints: action.payload.totalUsedPoints,
            timestamp: new Date().toISOString(),
          });
        }
      })
      .addCase(updateUserPoints.rejected, (state, action) => {
        log(LogLevel.ERROR, "[userSlice] updateUserPoints.rejected:", {
          error: action.payload,
          timestamp: new Date().toISOString(),
        });
        // 포인트 업데이트 실패는 전역 에러로 처리하지 않음 (백그라운드 작업)
      });
  },
});

// 액션 내보내기
export const { setUser, resetUser, clearError } = userSlice.actions;

// 리듀서 내보내기
export default userSlice.reducer;
